import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useAuth } from "../../utils/authContext.js";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import {
    fetchAdditionGroups,
    createAdditionGroup,
    updateAdditionGroup,
    deleteAdditionGroup,
    fetchDishAdditions,
    saveDishAddition,
    updateDishAddition,
    deleteDishAddition
} from "../../services/menu-service.js";
import { translateToRuAndEn } from "../../utils/googleTranslate.js";
import LoadingSpinner from "../../components/LoadingSpinner/loadingSpinner.js";
import Button from "../../components/comman/button.js";
import ConfirmationModal from "../../components/Modal/ConfirmationModal.jsx";
import Toast from "../../components/Toast/toast.js";
import CustomSelect from "../../components/comman/select.js";

const Configurations = () => {
    const { chefData } = useAuth();
    const { language } = useLanguage();
    const toastRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [groups, setGroups] = useState([]);
    const [additions, setAdditions] = useState([]);
    const isFetchingRef = useRef(false);
    
    // Group form state
    const [groupForm, setGroupForm] = useState({
        id: null,
        nameHy: "",
        nameRu: "",
        nameEn: "",
        isMulti: false,
        isRequired: false
    });
    const [isSavingGroup, setIsSavingGroup] = useState(false);
    const [isTranslatingGroup, setIsTranslatingGroup] = useState(false);
    const [showGroupForm, setShowGroupForm] = useState(false);
    
    // Addition form state
    const [additionForm, setAdditionForm] = useState({
        id: null,
        nameHy: "",
        nameRu: "",
        nameEn: "",
        price: "",
        groupId: ""
    });
    const [isSavingAddition, setIsSavingAddition] = useState(false);
    const [isTranslatingAddition, setIsTranslatingAddition] = useState(false);
    
    // Confirmation modals
    const [confirmDelete, setConfirmDelete] = useState({ open: false, targetId: null, type: null });
    const [rowLoading, setRowLoading] = useState(null);

    const fetchData = useCallback(async () => {
        // Prevent duplicate calls (e.g., from React.StrictMode in development)
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        setLoading(true);
        try {
            const [groupsData, additionsData] = await Promise.all([
                fetchAdditionGroups(),
                fetchDishAdditions()
            ]);
            setGroups(groupsData || []);
            setAdditions(additionsData || []);
        } catch (error) {
            console.error("Error fetching configurations:", error);
            toastRef.current?.addToast(
                getTranslation('configurations.fetchError', language) || "Failed to load configurations",
                "error"
            );
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [language]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Group by ID lookup
    const groupById = useMemo(() => {
        return groups.reduce((acc, group) => {
            acc[group.id] = group;
            return acc;
        }, {});
    }, [groups]);

    // Group additions by their groups
    const additionsByGroup = useMemo(() => {
        const grouped = {};
        grouped["ungrouped"] = [];
        
        additions.forEach((addition) => {
            const key = addition.group?.id ?? "ungrouped";
            if (!grouped[key]) {
                grouped[key] = [];
            }
            grouped[key].push(addition);
        });
        
        return grouped;
    }, [additions]);

    // Check if group is chef-owned
    const isChefGroup = (group) => {
        return group.chefId !== null && group.chefId === chefData?.id;
    };

    // Group validation
    const isGroupValid = () => {
        return groupForm.nameHy.trim() && 
               groupForm.nameRu.trim() && 
               groupForm.nameEn.trim();
    };

    // Addition validation
    const isAdditionValid = () => {
        return additionForm.nameHy.trim() && 
               additionForm.nameRu.trim() && 
               additionForm.nameEn.trim() &&
               additionForm.price !== "";
    };

    // Reset group form
    const resetGroupForm = () => {
        setGroupForm({
            id: null,
            nameHy: "",
            nameRu: "",
            nameEn: "",
            isMulti: false,
            isRequired: false
        });
        setShowGroupForm(false);
    };

    // Reset addition form
    const resetAdditionForm = () => {
        setAdditionForm({
            id: null,
            nameHy: "",
            nameRu: "",
            nameEn: "",
            price: "",
            groupId: ""
        });
    };

    // Translate group
    const handleTranslateGroup = async () => {
        if (!groupForm.nameHy.trim()) {
            toastRef.current?.addToast(
                getTranslation('toast.enterArmenianFirst', language) || "Please enter Armenian name first",
                "warning"
            );
            return;
        }
        setIsTranslatingGroup(true);
        try {
            const translations = await translateToRuAndEn(groupForm.nameHy);
            setGroupForm(prev => ({
                ...prev,
                nameRu: translations.ru,
                nameEn: translations.en
            }));
            toastRef.current?.addToast(
                getTranslation('toast.translationSuccess', language) || "Translation completed successfully!",
                "success"
            );
        } catch (error) {
            toastRef.current?.addToast(
                getTranslation('toast.translationFailed', language) || "Failed to translate. Please try again.",
                "error"
            );
        } finally {
            setIsTranslatingGroup(false);
        }
    };

    // Translate addition
    const handleTranslateAddition = async () => {
        if (!additionForm.nameHy.trim()) {
            toastRef.current?.addToast(
                getTranslation('toast.enterArmenianFirst', language) || "Please enter Armenian name first",
                "warning"
            );
            return;
        }
        setIsTranslatingAddition(true);
        try {
            const translations = await translateToRuAndEn(additionForm.nameHy);
            setAdditionForm(prev => ({
                ...prev,
                nameRu: translations.ru,
                nameEn: translations.en
            }));
            toastRef.current?.addToast(
                getTranslation('toast.translationSuccess', language) || "Translation completed successfully!",
                "success"
            );
        } catch (error) {
            toastRef.current?.addToast(
                getTranslation('toast.translationFailed', language) || "Failed to translate. Please try again.",
                "error"
            );
        } finally {
            setIsTranslatingAddition(false);
        }
    };

    // Save group
    const handleSaveGroup = async () => {
        if (!isGroupValid()) {
            toastRef.current?.addToast(
                getTranslation('configurations.allNamesRequired', language) || "All group names are required",
                "warning"
            );
            return;
        }
        setIsSavingGroup(true);
        try {
            const payload = {
                nameHy: groupForm.nameHy.trim(),
                nameRu: groupForm.nameRu.trim(),
                nameEn: groupForm.nameEn.trim(),
                isMulti: !!groupForm.isMulti,
                isRequired: !!groupForm.isRequired
            };
            
            let successMessage = "";
            if (groupForm.id) {
                await updateAdditionGroup(groupForm.id, payload);
                successMessage = getTranslation('configurations.groupUpdated', language) || "Group updated successfully";
            } else {
                await createAdditionGroup(payload);
                successMessage = getTranslation('configurations.groupCreated', language) || "Group created successfully";
            }
            await fetchData();
            // Defer toast until after React re-renders and Toast component is back in DOM
            setTimeout(() => {
                toastRef.current?.addToast(
                    successMessage,
                    "success"
                );
            }, 0);
            resetGroupForm();
        } catch (error) {
            toastRef.current?.addToast(
                getTranslation('configurations.groupSaveError', language) || "Failed to save group",
                "error"
            );
        } finally {
            setIsSavingGroup(false);
        }
    };

    // Edit group
    const handleEditGroup = (group) => {
        if (!isChefGroup(group)) {
            toastRef.current?.addToast(
                getTranslation('configurations.cannotEditGlobal', language) || "Cannot edit global groups",
                "warning"
            );
            return;
        }
        setGroupForm({
            id: group.id,
            nameHy: group.nameHy || "",
            nameRu: group.nameRu || "",
            nameEn: group.nameEn || "",
            isMulti: !!group.isMulti,
            isRequired: !!group.isRequired
        });
        setShowGroupForm(true);
    };

    // Delete group
    const handleDeleteGroup = async () => {
        if (!confirmDelete.targetId) return;
        setRowLoading(confirmDelete.targetId);
        try {
            await deleteAdditionGroup(confirmDelete.targetId);
            await fetchData();
            // Defer toast until after React re-renders and Toast component is back in DOM
            const deleteMsg = getTranslation('configurations.groupDeleted', language) || "Group deleted successfully";
            setTimeout(() => {
                toastRef.current?.addToast(
                    deleteMsg,
                    "success"
                );
            }, 0);
        } catch (error) {
            if (error?.response?.data?.code === "used-addition-group-id") {
                toastRef.current?.addToast(
                    getTranslation('configurations.groupInUse', language) || "Cannot delete group because it has associated additions. Please remove or reassign additions first.",
                    "error"
                );
            } else {
                toastRef.current?.addToast(
                    getTranslation('configurations.groupDeleteError', language) || "Failed to delete group",
                    "error"
                );
            }
        } finally {
            setRowLoading(null);
            setConfirmDelete({ open: false, targetId: null, type: null });
        }
    };

    // Save addition
    const handleSaveAddition = async () => {
        if (!isAdditionValid()) {
            toastRef.current?.addToast(
                getTranslation('configurations.allAdditionFieldsRequired', language) || "All addition fields are required",
                "warning"
            );
            return;
        }
        setIsSavingAddition(true);
        try {
            const payload = {
                nameHy: additionForm.nameHy.trim(),
                nameRu: additionForm.nameRu.trim(),
                nameEn: additionForm.nameEn.trim(),
                price: parseFloat(additionForm.price),
                group: additionForm.groupId ? { id: Number(additionForm.groupId) } : null
            };
            
            if (additionForm.id) {
                await updateDishAddition({ id: additionForm.id, ...payload });
                toastRef.current?.addToast(
                    getTranslation('configurations.additionUpdated', language) || "Addition updated successfully",
                    "success"
                );
            } else {
                await saveDishAddition(payload);
                toastRef.current?.addToast(
                    getTranslation('configurations.additionCreated', language) || "Addition created successfully",
                    "success"
                );
            }
            await fetchData();
            resetAdditionForm();
        } catch (error) {
            toastRef.current?.addToast(
                getTranslation('configurations.additionSaveError', language) || "Failed to save addition",
                "error"
            );
        } finally {
            setIsSavingAddition(false);
        }
    };

    // Edit addition
    const handleEditAddition = (addition) => {
        setAdditionForm({
            id: addition.id,
            nameHy: addition.nameHy || "",
            nameRu: addition.nameRu || "",
            nameEn: addition.nameEn || "",
            price: addition.price || "",
            groupId: addition.group?.id ? String(addition.group.id) : ""
        });
    };

    // Delete addition
    const handleDeleteAddition = async () => {
        if (!confirmDelete.targetId) return;
        setRowLoading(confirmDelete.targetId);
        try {
            await deleteDishAddition(confirmDelete.targetId);
            toastRef.current?.addToast(
                getTranslation('configurations.additionDeleted', language) || "Addition deleted successfully",
                "success"
            );
            await fetchData();
        } catch (error) {
            toastRef.current?.addToast(
                getTranslation('configurations.additionDeleteError', language) || "Failed to delete addition",
                "error"
            );
        } finally {
            setRowLoading(null);
            setConfirmDelete({ open: false, targetId: null, type: null });
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <>
            <div className="transition-all duration-300">
                <h1 className="text-xl font-bold mb-4 text-gray-900 dark:text-[#c9d1d9]">
                    {getTranslation('configurations.pageTitle', language) || 'Կարգավորումներ'}
                </h1>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Addition Groups Section */}
                    <div className="bg-white dark:bg-[#161b22] rounded-lg shadow-md p-6 border dark:border-[#30363d]">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-[#c9d1d9]">
                                {getTranslation('configurations.additionGroups', language) || 'Հավելումների խմբեր'}
                            </h2>
                            {!showGroupForm && (
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={() => setShowGroupForm(true)}
                                >
                                    {getTranslation('configurations.addGroup', language) || '+ Ավելացնել խումբ'}
                                </Button>
                            )}
                        </div>

                        {showGroupForm && (
                            <div className="mb-6 p-4 bg-gray-50 dark:bg-[#0d1117] rounded-lg border dark:border-[#30363d]">
                                <h3 className="text-md font-semibold mb-4 text-gray-900 dark:text-[#c9d1d9]">
                                    {groupForm.id 
                                        ? getTranslation('configurations.editGroup', language) || 'Խմբագրել խումբ'
                                        : getTranslation('configurations.newGroup', language) || 'Նոր խումբ'}
                                </h3>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c9d1d9] mb-1">
                                            {getTranslation('configurations.nameArmenian', language) || 'Անուն (Հայերեն)'} *
                                        </label>
                                        <input
                                            type="text"
                                            value={groupForm.nameHy}
                                            onChange={(e) => setGroupForm(prev => ({ ...prev, nameHy: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-[#30363d] rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9]"
                                            placeholder={getTranslation('configurations.nameArmenian', language) || 'Անուն (Հայերեն)'}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c9d1d9] mb-1">
                                            {getTranslation('configurations.nameRussian', language) || 'Անուն (Ռուսերեն)'} *
                                        </label>
                                        <input
                                            type="text"
                                            value={groupForm.nameRu}
                                            onChange={(e) => setGroupForm(prev => ({ ...prev, nameRu: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-[#30363d] rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9]"
                                            placeholder={getTranslation('configurations.nameRussian', language) || 'Անուն (Ռուսերեն)'}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c9d1d9] mb-1">
                                            {getTranslation('configurations.nameEnglish', language) || 'Անուն (Անգլերեն)'} *
                                        </label>
                                        <input
                                            type="text"
                                            value={groupForm.nameEn}
                                            onChange={(e) => setGroupForm(prev => ({ ...prev, nameEn: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-[#30363d] rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9]"
                                            placeholder={getTranslation('configurations.nameEnglish', language) || 'Անուն (Անգլերեն)'}
                                        />
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={groupForm.isMulti}
                                                onChange={(e) => setGroupForm(prev => ({ ...prev, isMulti: e.target.checked }))}
                                                className="mr-2"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-[#c9d1d9]">
                                                {getTranslation('configurations.allowMultiple', language) || 'Թույլատրել բազմակի ընտրություն'}
                                            </span>
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={groupForm.isRequired}
                                                onChange={(e) => setGroupForm(prev => ({ ...prev, isRequired: e.target.checked }))}
                                                className="mr-2"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-[#c9d1d9]">
                                                {getTranslation('configurations.required', language) || 'Պարտադիր'}
                                            </span>
                                        </label>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={handleTranslateGroup}
                                            disabled={isTranslatingGroup || !groupForm.nameHy.trim()}
                                            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition-colors duration-200 flex items-center gap-1"
                                            title={getTranslation('configurations.translate', language) || 'Translate'}
                                        >
                                            {isTranslatingGroup ? (
                                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            ) : (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                                </svg>
                                            )}
                                            {getTranslation('configurations.translate', language) || 'Թարգմանել'}
                                        </button>
                                        <div className="flex-1" />
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={resetGroupForm}
                                        >
                                            {getTranslation('common.cancel', language) || 'Չեղարկել'}
                                        </Button>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={handleSaveGroup}
                                            disabled={!isGroupValid() || isSavingGroup}
                                            loading={isSavingGroup}
                                        >
                                            {groupForm.id 
                                                ? getTranslation('common.update', language) || 'Թարմացնել'
                                                : getTranslation('common.create', language) || 'Ստեղծել'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                            {groups.length === 0 ? (
                                <p className="text-center text-gray-500 dark:text-[#8b949e] py-4">
                                    {getTranslation('configurations.noGroups', language) || 'Խմբեր չկան'}
                                </p>
                            ) : (
                                groups.map((group) => (
                                    <div
                                        key={group.id}
                                        className="p-3 border border-gray-200 dark:border-[#30363d] rounded-lg bg-gray-50 dark:bg-[#0d1117]"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-semibold text-gray-900 dark:text-[#c9d1d9]">
                                                        {group[`name${language.charAt(0).toUpperCase() + language.slice(1)}`] || group.nameHy}
                                                    </span>
                                                    {isChefGroup(group) ? (
                                                        <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded">
                                                            {getTranslation('configurations.myGroup', language) || 'Իմ խումբ'}
                                                        </span>
                                                    ) : (
                                                        <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded">
                                                            {getTranslation('configurations.global', language) || 'Գլոբալ'}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-[#8b949e] space-x-2">
                                                    <span>{group.isMulti ? getTranslation('configurations.multiple', language) || 'Բազմակի' : getTranslation('configurations.single', language) || 'Մեկ'}</span>
                                                    <span>•</span>
                                                    <span>{group.isRequired ? getTranslation('configurations.required', language) || 'Պարտադիր' : getTranslation('configurations.optional', language) || 'Ընտրովի'}</span>
                                                </div>
                                            </div>
                                            {isChefGroup(group) && (
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleEditGroup(group)}
                                                        className="p-1 text-gray-700 dark:text-[#b1bac4] hover:text-gray-900 dark:hover:text-[#c9d1d9] transition-colors"
                                                        title={getTranslation('common.edit', language) || 'Խմբագրել'}
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => setConfirmDelete({ open: true, targetId: group.id, type: 'group' })}
                                                        disabled={rowLoading === group.id}
                                                        className="p-1 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-500 transition-colors disabled:opacity-50"
                                                        title={getTranslation('common.delete', language) || 'Ջնջել'}
                                                    >
                                                        {rowLoading === group.id ? (
                                                            <LoadingSpinner size="small" />
                                                        ) : (
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Additions Section */}
                    <div className="bg-white dark:bg-[#161b22] rounded-lg shadow-md p-6 border dark:border-[#30363d]">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-[#c9d1d9]">
                                {getTranslation('configurations.additions', language) || 'Հավելումներ'}
                            </h2>
                        </div>

                        {/* Addition Form */}
                        <div className="mb-6 p-4 bg-gray-50 dark:bg-[#0d1117] rounded-lg border dark:border-[#30363d]">
                            <h3 className="text-md font-semibold mb-4 text-gray-900 dark:text-[#c9d1d9]">
                                {additionForm.id 
                                    ? getTranslation('configurations.editAddition', language) || 'Խմբագրել հավելում'
                                    : getTranslation('configurations.newAddition', language) || 'Նոր հավելում'}
                            </h3>
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c9d1d9] mb-1">
                                            {getTranslation('configurations.nameArmenian', language) || 'Անուն (Հայերեն)'} *
                                        </label>
                                        <input
                                            type="text"
                                            value={additionForm.nameHy}
                                            onChange={(e) => setAdditionForm(prev => ({ ...prev, nameHy: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-[#30363d] rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9]"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c9d1d9] mb-1">
                                            {getTranslation('configurations.price', language) || 'Գին'} *
                                        </label>
                                        <input
                                            type="number"
                                            value={additionForm.price}
                                            onChange={(e) => setAdditionForm(prev => ({ ...prev, price: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-[#30363d] rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9]"
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c9d1d9] mb-1">
                                            {getTranslation('configurations.nameRussian', language) || 'Անուն (Ռուսերեն)'} *
                                        </label>
                                        <input
                                            type="text"
                                            value={additionForm.nameRu}
                                            onChange={(e) => setAdditionForm(prev => ({ ...prev, nameRu: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-[#30363d] rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9]"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c9d1d9] mb-1">
                                            {getTranslation('configurations.nameEnglish', language) || 'Անուն (Անգլերեն)'} *
                                        </label>
                                        <input
                                            type="text"
                                            value={additionForm.nameEn}
                                            onChange={(e) => setAdditionForm(prev => ({ ...prev, nameEn: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-[#30363d] rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9]"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <CustomSelect
                                        label={getTranslation('configurations.group', language) || 'Խումբ'}
                                        value={additionForm.groupId ? String(additionForm.groupId) : null}
                                        onChange={(selected) => setAdditionForm(prev => ({ ...prev, groupId: selected?.value || "" }))}
                                        options={[
                                            { value: "", label: getTranslation('configurations.noGroup', language) || 'Խումբ չկա' },
                                            ...groups.map(group => ({
                                                value: String(group.id),
                                                label: `${group[`name${language.charAt(0).toUpperCase() + language.slice(1)}`] || group.nameHy}${isChefGroup(group) ? ` (${getTranslation('configurations.myGroup', language) || 'Իմ խումբ'})` : ` (${getTranslation('configurations.global', language) || 'Գլոբալ'})`}`
                                            }))
                                        ]}
                                        placeholder={getTranslation('configurations.noGroup', language) || 'Խումբ չկա'}
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleTranslateAddition}
                                        disabled={isTranslatingAddition || !additionForm.nameHy.trim()}
                                        className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition-colors duration-200 flex items-center gap-1"
                                        title={getTranslation('configurations.translate', language) || 'Translate'}
                                    >
                                        {isTranslatingAddition ? (
                                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        ) : (
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                            </svg>
                                        )}
                                        {getTranslation('configurations.translate', language) || 'Թարգմանել'}
                                    </button>
                                    <div className="flex-1" />
                                    {additionForm.id && (
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={resetAdditionForm}
                                        >
                                            {getTranslation('common.cancel', language) || 'Չեղարկել'}
                                        </Button>
                                    )}
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleSaveAddition}
                                        disabled={!isAdditionValid() || isSavingAddition}
                                        loading={isSavingAddition}
                                    >
                                        {additionForm.id 
                                            ? getTranslation('common.update', language) || 'Թարմացնել'
                                            : getTranslation('common.create', language) || 'Ստեղծել'}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Additions List */}
                        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                            {Object.entries(additionsByGroup).map(([groupId, groupAdditions]) => {
                                const group = groupId !== "ungrouped" ? groupById[groupId] : null;
                                return (
                                    <div key={groupId}>
                                        <h3 className="text-md font-semibold mb-2 text-gray-900 dark:text-[#c9d1d9]">
                                            {group 
                                                ? group[`name${language.charAt(0).toUpperCase() + language.slice(1)}`] || group.nameHy
                                                : getTranslation('configurations.ungrouped', language) || 'Չխմբավորված'}
                                        </h3>
                                        {groupAdditions.length === 0 ? (
                                            <p className="text-sm text-gray-500 dark:text-[#8b949e] py-2">
                                                {getTranslation('configurations.noAdditionsInGroup', language) || 'Հավելումներ չկան'}
                                            </p>
                                        ) : (
                                            <div className="space-y-2">
                                                {groupAdditions.map((addition) => (
                                                    <div
                                                        key={addition.id}
                                                        className="p-3 border border-gray-200 dark:border-[#30363d] rounded-lg bg-gray-50 dark:bg-[#0d1117] flex justify-between items-center"
                                                    >
                                                        <div className="flex-1">
                                                            <div className="font-medium text-gray-900 dark:text-[#c9d1d9]">
                                                                {addition[`name${language.charAt(0).toUpperCase() + language.slice(1)}`] || addition.nameHy}
                                                            </div>
                                                            <div className="text-sm text-gray-500 dark:text-[#8b949e]">
                                                                {addition.price} AMD
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => handleEditAddition(addition)}
                                                                className="p-1 text-gray-700 dark:text-[#b1bac4] hover:text-gray-900 dark:hover:text-[#c9d1d9] transition-colors"
                                                                title={getTranslation('common.edit', language) || 'Խմբագրել'}
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={() => setConfirmDelete({ open: true, targetId: addition.id, type: 'addition' })}
                                                                disabled={rowLoading === addition.id}
                                                                className="p-1 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-500 transition-colors disabled:opacity-50"
                                                                title={getTranslation('common.delete', language) || 'Ջնջել'}
                                                            >
                                                                {rowLoading === addition.id ? (
                                                                    <LoadingSpinner size="small" />
                                                                ) : (
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                    </svg>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            {confirmDelete.open && (
                <ConfirmationModal
                    description={
                        confirmDelete.type === 'group'
                            ? getTranslation('configurations.confirmDeleteGroup', language) || 'Are you sure you want to delete this group?'
                            : getTranslation('configurations.confirmDeleteAddition', language) || 'Are you sure you want to delete this addition?'
                    }
                    onCancel={() => setConfirmDelete({ open: false, targetId: null, type: null })}
                    onSubmit={confirmDelete.type === 'group' ? handleDeleteGroup : handleDeleteAddition}
                />
            )}

            <Toast ref={toastRef} />
        </>
    );
};

export default Configurations;

